import React from "react";
import clsx from "clsx";
import Dropdown from "@components/FilterComponent/components/DropDownComponent";
import EditableInputComponent from "@components/EditableInputComponent";
import { FIRST_INDEX, ZERO_VALUE } from "./constants";

/**
 * VarianceRow component - renders a single corrective action row with editable amounts and variance display
 * @param {object} singleAction - The corrective action data
 * @param {number} index - Index of the current action
 * @param {array} correctiveAction - Array of all corrective actions
 * @param {object} filterValues - Filter configuration for dropdown
 * @param {number} sourceAmount - Current source amount
 * @param {number} destinationAmount - Current destination amount
 * @param {function} setSourceAmount - Handler to update source amount
 * @param {function} setDestinationAmount - Handler to update destination amount
 * @param {function} setSelectedCorrectiveReason - Handler to update selected reason
 * @param {array} selectedCorrectiveReason - Array of selected reasons
 * @param {function} translate - Translation function
 * @param {element} renderSection - Section renderer component
 * @param {function} setIsEditing - Handler to update editing state
 */
const VarianceRow = ({
  singleAction,
  index,
  correctiveAction,
  filterValues,
  sourceAmount,
  destinationAmount,
  setSourceAmount,
  setDestinationAmount,
  setSelectedCorrectiveReason,
  selectedCorrectiveReason,
  translate,
  renderSection,
  setIsEditing,
}) => {
  const keyToPass = !!singleAction.ValueVariance
    ? "ValueVariance"
    : "AmountVariance";
  const variance = singleAction.ValueVariance || singleAction.AmountVariance;
  const isManuallyCleared =
    selectedCorrectiveReason[index]?.CorrectionResultTag ===
    "MANUALLY_CLEARED";
  const calculatedVariance =
    (destinationAmount || singleAction?.DestinationAmount) -
    (sourceAmount || singleAction?.SourceAmount);

  const mainVariance =
    singleAction?.DestinationAmount - singleAction?.SourceAmount;

  const displayValues = {
    CorrectionResultName: (
      <div className="mb-2">
        <div className="flex gap-3 items-baseline">
          <span>{translate("corrective-action.CorrectionResultName")}</span>
        </div>
      </div>
    ),
    DestinationAmount: (
      <div>
        <div className="flex gap-1 items-baseline">
          <span>{translate("corrective-action.DestinationAmount")}</span>
        </div>
      </div>
    ),
    SourceAmount: (
      <div>
        <div className="flex gap-1 items-baseline">
          <span>{translate("corrective-action.SourceAmount")}</span>
        </div>
      </div>
    ),
    AmountVariance: (
      <div>
        <div className="flex gap-1 items-baseline">
          {translate("corrective-action.Variance")}
        </div>
        <span
          className={clsx(
            "text-xs cursor-pointer text-primary-600",
            mainVariance === calculatedVariance && "invisible",
            !isManuallyCleared && "hidden"
          )}
        >
          {translate("confirmation-popup.originalVariance")}
          {mainVariance || ZERO_VALUE}
        </span>
      </div>
    ),
    ValueVariance: (
      <div className="flex gap-1 items-baseline">
        {translate("corrective-action.Variance")}
        {isManuallyCleared && calculatedVariance !== variance && (
          <span className="text-primary-600 text-xs font-semibold">
            {translate("corrective-action.modified")}
          </span>
        )}
      </div>
    ),
  };

  const customValues = {
    CorrectionResultName: (
      <span className="font-semibold ">
        <Dropdown
          key={singleAction + index}
          filter={filterValues}
          handleChange={(e, filter) => {
            const selectedOption = filter.values.find(
              (option) => option.value === e.target.value
            );
            const updatedCorrectiveActions = [
              ...selectedCorrectiveReason,
            ];
            updatedCorrectiveActions[index] = {
              CorrectionResultId:
                correctiveAction[index]?.CorrectionResultId,
              CorrectionResultName: selectedOption?.displayName,
              CorrectionResultTag: selectedOption?.value,
            };
            setSelectedCorrectiveReason(updatedCorrectiveActions);
          }}
          selectedOption={filterValues.values.find(
            (option) =>
              option.value ===
              selectedCorrectiveReason[index]?.CorrectionResultTag
          )}
          index={index}
          className={
            filterValues.values.find(
              (option) =>
                option.value ===
                selectedCorrectiveReason[index]?.CorrectionResultTag
            )?.className ||
            filterValues.values[FIRST_INDEX].className
          }
        />
      </span>
    ),
    DestinationAmount: isManuallyCleared ? (
      <EditableInputComponent
        updatedValue={destinationAmount || ""}
        onSave={(value) =>
          setDestinationAmount((prev) => {
            const updatedAmount = [...prev];
            updatedAmount[index] = value || null;
            return updatedAmount;
          })
        }
        onCancel={() =>
          setDestinationAmount((prev) => {
            const updatedAmount = [...prev];
            updatedAmount[index] =
              singleAction?.DestinationAmount || null;
            return updatedAmount;
          })
        }
        hasToolTip={false}
        hasLabel={false}
        className=""
        originalAmount={singleAction.DestinationAmount || ""}
        setIsEditingParent={setIsEditing}
      />
    ) : !!singleAction.DestinationAmount ? (
      `${singleAction?.DestinationAmount} ${singleAction?.Currency}`
    ) : (
      "N/A"
    ),
    SourceAmount: isManuallyCleared ? (
      <EditableInputComponent
        updatedValue={sourceAmount || ""}
        onSave={(value) =>
          setSourceAmount((prev) => {
            const updatedAmount = [...prev];
            updatedAmount[index] = value || null;
            return updatedAmount;
          })
        }
        onCancel={() =>
          setSourceAmount((prev) => {
            const updatedAmount = [...prev];
            updatedAmount[index] =
              singleAction?.SourceAmount || null;
            return updatedAmount;
          })
        }
        hasToolTip={false}
        hasLabel={false}
        className=""
        originalAmount={singleAction.SourceAmount || ""}
        setIsEditingParent={setIsEditing}
      />
    ) : !!singleAction.SourceAmount ? (
      `${singleAction?.SourceAmount} ${singleAction?.Currency}`
    ) : (
      "N/A"
    ),
    AmountVariance: (
      <span
        className={`font-semibold ${
          !parseInt(calculatedVariance) || !variance
            ? "text-green-600"
            : "text-red-600"
        }`}
      >
        {calculatedVariance} {singleAction?.Currency}
      </span>
    ),
    ValueVariance: (
      <span
        className={`font-semibold ${
          !parseInt(calculatedVariance) || !variance
            ? "text-green-600"
            : "text-red-600"
        }`}
      >
        {calculatedVariance} {singleAction?.Currency}
      </span>
    ),
  };

  return {
    displayValues,
    customValues,
    keyToPass,
    isManuallyCleared,
    calculatedVariance,
  };
};

export default VarianceRow;
